import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { AI_BOT_CONFIG, BASE_URI, config } from 'app/app.config';
import { BehaviorSubject, Observable, Subject, map } from 'rxjs';

declare var annyang: any;

export enum aiVoiceState {
  Idle = 'idle',
  Listening = 'listening',
  Speaking = 'speaking',
  Thinking = 'thinking',
  Stopped = 'stopped'
}


export type AiVoiceState = 'idle' | 'listening' | 'speaking' | 'thinking' | 'stopped';

export interface QItem {
  question_id: number;
  question: string;
  answer: string;
}

export interface caption {
  id: string;
  text: string;
  isFinal: boolean;
  timestramp: number;
}

@Injectable({
  providedIn: 'root'
})
export class BotService {

  baseUrl = BASE_URI.URL;

  public aiBaseURL = AI_BOT_CONFIG.agent.llmUrl;
  historyList: any[] = [];
  // synth = window.speechSynthesis;
  voice: SpeechSynthesisVoice[] = [];
  state: aiVoiceState = aiVoiceState.Idle;
  private captionSubject$ = new Subject<caption>();
  caption$ = this.captionSubject$.asObservable();

  public state$ = new BehaviorSubject<AiVoiceState>('idle');
  private synth = typeof window !== 'undefined' ? window.speechSynthesis : null;


  private dataUrl = 'assets/js/agenticai.json';
  private BaseURL = BASE_URI.URL;
  private currentUtterances: SpeechSynthesisUtterance[] = [];
  private utteranceSeq = 0;

  recognitionActive = false;
  onResultCallback: ((phrases: string[], event?: any) => void) | null = null;
  onEndCallback: (() => void) | null = null;
  onResultMatchCallback: ((userSaid: string, commandText: string, phrases: string[]) => void) | null = null;

  constructor(
    private zone: NgZone,
    private http: HttpClient
  ) {

    if (annyang) {
      // Debug mode (optional)
      annyang.debug(true);
    }

    this.loadVoices();

    window.speechSynthesis.onvoiceschanged = () => {
      const voices = speechSynthesis.getVoices();
    };
  }

  get currentState(): AiVoiceState {
    return this.state$.value;
  }

  public loadVoices() {
    const voices = this.synth.getVoices();
    if (voices.length) {
      this.voice = voices;
    } else {
      this.synth.onvoiceschanged = () => {
        this.voice = this.synth.getVoices();
      }
    }
  }

  speak(
    text: string,
    voiceName?: string,
    rate = 1,
    pitch = 1,
    lang?: string,
    callback?: () => void
  ) {
    if (!text) return;

    if (!this.synth) {
      this.emitCaption(text, true);
      return;
    }

    this.stopSpeaking();
    this.state = aiVoiceState.Speaking;
    this.synth.cancel();

    this.currentUtterances = [];
    this.utteranceSeq++;

    const utterance = new SpeechSynthesisUtterance(text);
    if (lang) utterance.lang = lang;
    utterance.rate = rate;
    utterance.pitch = pitch;


    // Voice selection
    if (voiceName) {
      const voices = this.synth.getVoices();

      // Try to find a Microsoft female voice in en-US
      const microsoftFemaleVoices = voices.filter(v =>
        v.lang.toLowerCase().startsWith('en-us') &&
        /microsoft/i.test(v.name) &&
        (/female/i.test(v.name) || /zira/i.test(v.name)) // common MS female voice names
      );

      if (microsoftFemaleVoices.length > 0) {
        utterance.voice = microsoftFemaleVoices[0];
      } else {

        utterance.voice = voices.find(v => v.name === voiceName || v.lang === voiceName) ?? voices[0];
      }
    }

    // Streaming captions with boundary support
    if ('onboundary' in SpeechSynthesisUtterance.prototype) {
      const captionBuffer: string[] = [];
      utterance.onboundary = (event: SpeechSynthesisEvent) => {
        const charIndex = typeof event.charIndex === 'number' ? event.charIndex : null;
        if (charIndex !== null) {
          const textSoFar = text.slice(0, charIndex + 1);
          const chunks = textSoFar.split(/(?<=[.!?])\s+|\n/);
          const lastChunk = (chunks.length > 0 ? chunks[chunks.length - 1] : textSoFar).trim();

          if (lastChunk && lastChunk !== captionBuffer[captionBuffer.length - 1]) {
            this.zone.run(() => this.emitCaption(lastChunk, false));
            captionBuffer.push(lastChunk);
          }
        }
      };
    } else {
      const chunks = this.splitToChunks(text);
      this.speakChunksSequentially(chunks, utterance.voice ?? undefined, lang);
      return;
    }

    // Events
    utterance.onstart = () => {
      this.state = aiVoiceState.Speaking;
      const firstChunk = this.splitToChunks(text)[0] ?? text;
      this.emitCaption(firstChunk.trim(), false);
    };

    utterance.onend = () => {
      this.state = aiVoiceState.Idle;
      this.emitCaption(text.trim(), true);
      callback?.();
    };

    utterance.onerror = () => {
      this.state = aiVoiceState.Idle;
      this.emitCaption(text.trim(), true);
    };

    this.zone.runOutsideAngular(() => {
      this.currentUtterances.push(utterance);
      this.synth!.speak(utterance);
    });
  }

  splitToChunks(text: string): string[] {
    const rawText = text.split(/(?<=[.!?])\s+|\n/).map(s => s.trim()).filter(Boolean);
    const maxLength = 120;

    const out: string[] = [];

    for (const x of rawText) {
      if (x.length <= maxLength) {
        out.push(x);
      } else {
        const words = x.split(/\s+/);
        let cur = '';

        for (const w of words) {
          if ((cur + ' ' + w).trim().length <= maxLength) {
            cur = (cur + ' ' + w).trim();
          } else {
            if (cur) out.push(cur);
            cur = w;
          }
        }
        if (cur) out.push(cur);

      }
    }

    return out;
  }

  private speakChunksSequentially(chunks: string[], voice: SpeechSynthesisVoice | undefined, lang?: string) {
    if (!this.synth) return;
    const speakNext = (index: number) => {
      if (index >= chunks.length) {
        const combined = chunks.join(' ').trim();
        this.emitCaption(combined, true);
        return;
      }
      const u = new SpeechSynthesisUtterance(chunks[index]);
      if (lang) u.lang = lang;
      if (voice) u.voice = voice;
      u.rate = 1;
      u.pitch = 1;

      u.onstart = () => {
        this.emitCaption(chunks[index], index === chunks.length - 1);
      };
      u.onend = () => speakNext(index + 1);
      u.onerror = () => speakNext(index + 1);

      this.zone.runOutsideAngular(() => this.synth!.speak(u));
    };

    // cancel previous then start
    this.synth.cancel();
    speakNext(0);
  }

  emitCaption(text: string, isFinal: boolean) {
    const caption: caption = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      text: text,
      timestramp: Date.now(),
      isFinal: !!isFinal
    };

    this.zone.run(() => this.captionSubject$.next(caption));
  }



  stopSpeaking() {
    if (this.synth.speaking || this.synth.pending) {
      this.synth.cancel();
      this.state = aiVoiceState.Stopped;
    }
  }



  startListening(callback: (query: string, isFinal?: boolean, confidence?: number) => void) {

    if (!annyang) return;

    this.state = aiVoiceState.Listening;

    annyang.setLanguage('en-US');

    const CONFIDENCE_THRESHOLD = 0.6;

    this.onResultCallback = (phrases: string[], event?: any) => {

      let text = '';
      let confidence = 0;
      let isFinal = false;

      if (event?.results?.length) {
        const result = event.results[event.results.length - 1];

        isFinal = result.isFinal;

        if (result[0]) {
          text = result[0].transcript.trim();
          confidence = result[0].confidence;
        }
      } else {
        text = phrases[0]?.trim();
      }

      // Ignore noise
      if (!text || text.length < 2 || confidence < CONFIDENCE_THRESHOLD) {
        return;
      }

      this.setState(isFinal ? 'thinking' : 'listening');

      callback?.(text, isFinal, confidence);
    };

    this.onEndCallback = () => {
      this.state = aiVoiceState.Idle;
    };

    annyang.addCallback('result', this.onResultCallback);
    annyang.addCallback('end', this.onEndCallback);

    annyang.start({
      autoRestart: true,
      continuous: true
    });
  }

  stopListening() {
    if (!annyang) return;

    annyang.abort();

    if (this.onResultCallback) {
      annyang.removeCallback('result', this.onResultCallback);
      this.onResultCallback = null;
    }

    if (this.onEndCallback) {
      annyang.removeCallback('end', this.onEndCallback);
      this.onEndCallback = null;
    }

    this.recognitionActive = false;
    this.setState('stopped');
  }

  getAnswers(query: string): Observable<string | null> {
    return this.http.get<QItem[]>(this.dataUrl).pipe(
      map((items: QItem[]) => {
        const normalizedQuery = query.toLowerCase();
        // 1. Exact match
        const exactMatch:any = items.find(item =>
          item.question.toLowerCase() === normalizedQuery
        );

        return exactMatch.answer;

      })
    );
  }

  createHistory(payload: any) {
    return this.http.post(`${this.BaseURL}/history`, payload);
  }

  getAIHistoryData(payload: any) {

    const Params = this.preHttpParams(payload)

    return this.http.get(`${this.BaseURL}/history`, { params: Params });
  }

  deleteAIHistory(historyId: any) {
    return this.http.delete(`${this.BaseURL}/deleteHistory/${historyId}`);
  }

  llmModelStart() {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post(`${this.aiBaseURL}chat/start`, { headers: headers });
  }

  llmModelQuery(payload: any) {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    // const queryPayload = {
    //   message: payload.query
    // }

    return this.http.post(`${this.BaseURL}/getBotMessage`, payload, { headers: headers });

    // return this.http.post(`${this.aiBaseURL}chat/${payload.session_id}/message`, queryPayload, { headers: headers })
  }

  llmAIVoiceQuery(payload: any) {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    // const queryPayload = {
    //   message: payload.query
    // }

    return this.http.post(`${this.BaseURL}/aiVoice`, payload, { headers: headers });
    // return this.http.post(`${this.aiBaseURL}chat/${payload.session_id}/stream-tts`, queryPayload, { headers: headers })
  }

  llmModelHistory(payload: any) {
    return this.http.get(`${this.aiBaseURL}/chat/${payload.session_id}/history`);
  }

  aiVoiceCreateAudio(payload: any) {
    return this.http.post('/generateAiVoiceAudio', payload);
  }

  llmModelEnd(payload: any) {

    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(`${this.aiBaseURL}/chat/${payload.session_id}/end`, { headers: headers });
  }

  llmModelUserInfo() {
    return this.http.get(`${this.aiBaseURL}` + '/user-info');
  }

  llmModelGenericQuery(query: any) {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    var payload = {
      prompt: query
    }

    return this.http.post(`${this.aiBaseURL}` + 'query', payload, { headers: headers });
  }

  setState(newState: AiVoiceState): void {
    // console.log(`AI Voice state changed: ${this.currentState} → ${newState}`);
    this.state$.next(newState);
  }

  /** Reset to idle */
  reset(): void {
    this.setState('idle');
  }

  postBotMessage(payLoad: any) {
    return this.http.post(`${this.BaseURL}/bot-message`, payLoad);
  };

  preHttpParams(payload: any) {
    let Params = new HttpParams();
    for (const key in payload) {
      if (key && (payload[key] || payload[key] === 0)) {
        Params = Params.append(key, payload[key]);
      }
    }
    return Params;
  }

}
